import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Clock, AlertCircle, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { ScreenTitle } from "@/components/ScreenTitle";
import { inr, apiError } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function VerifyScreen() {
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [nameHi, setNameHi] = useState({});

  const load = async () => {
    const [d, w, m] = await Promise.all([
      api.get("/admin/deposits"),
      api.get("/admin/withdraw-requests"),
      api.get("/members"),
    ]);
    setDeposits(d.data);
    setWithdrawals(w.data);
    const map = {};
    m.data.forEach((x) => { map[x.id] = x.name_hi || x.name; });
    setNameHi(map);
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const autoSms = async (d) => {
    try {
      const { data } = await api.post("/sms-webhook", { message: `Rs ${d.amount} credited UTR ${d.utr}`, sender: "INDIE" });
      if (data.verified) { toast.success("⚡ ऑटो SMS सत्यापन सफल — जमा स्वीकृत"); load(); }
      else toast.error("SMS match nahi hua");
    } catch (e) { toast.error(apiError(e)); }
  };

  const pendingDeposits = deposits.filter((d) => d.status === "pending");
  const pendingWithdrawals = withdrawals.filter((w) => w.status === "pending");
  const totalPending = pendingDeposits.length + pendingWithdrawals.length;

  return (
    <div className="space-y-5">
      <ScreenTitle hi="सत्यापन स्क्रीन" en="एडमिन - लंबित भुगतान सत्यापन" />

      {/* Auto SMS banner */}
      <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4 text-center">
        <p className="font-display font-bold text-blue-900 flex items-center justify-center gap-1.5 flex-wrap">
          ऑटो SMS सत्यापन <BadgeCheck className="h-5 w-5 text-emerald-600" /> <span className="text-emerald-600">सफलतापूर्वक पूरा</span> <BadgeCheck className="h-5 w-5 text-emerald-600" />
        </p>
        <p className="text-xs text-blue-700 mt-1">SMS के माध्यम से लेन-देन स्वतः सत्यापित हो गया है</p>
      </div>

      {/* Pending heading */}
      <div className="flex items-center justify-between">
        <h2 className="font-display font-extrabold text-3xl text-blue-800">लंबित भुगतान</h2>
        <span className="px-3 py-1.5 rounded-xl bg-amber-100 text-amber-700 text-sm font-bold">{totalPending} लंबित</span>
      </div>

      {totalPending === 0 && <p className="text-center text-muted-foreground py-6 text-sm">कोई लंबित सत्यापन नहीं</p>}

      {/* Deposit cards */}
      {pendingDeposits.map((d) => (
        <div key={d.id} data-testid={`verify-deposit-${d.id}`} className="rounded-3xl bg-white p-5 shadow-md border border-slate-100">
          <div className="flex items-center gap-3">
            <Avatar name={d.member_name} size={52} />
            <p className="font-display font-bold text-2xl">{nameHi[d.member_id] || d.member_name}</p>
          </div>
          <div className="flex items-center justify-between mt-4">
            <p className="font-mono font-extrabold text-3xl text-blue-900">{inr(d.amount)}</p>
            <span className="px-3 py-1.5 rounded-xl bg-amber-100 text-amber-700 text-sm font-semibold flex items-center gap-1"><Clock className="h-4 w-4" /> लंबित</span>
          </div>
          <div className="border-t border-slate-100 mt-4 pt-3">
            <p className="text-muted-foreground text-sm">UTR: <span className="font-mono text-foreground text-base font-semibold">{d.utr}</span></p>
          </div>
          <div className="border-t border-slate-100 mt-3 pt-3">
            <p className="text-sm text-muted-foreground flex items-center gap-1 flex-wrap">भुगतान की स्थिति: <span className="text-amber-600 font-semibold">लंबित - सत्यापन आवश्यक</span> <AlertCircle className="h-4 w-4 text-amber-500" /></p>
          </div>
          <div className="mt-3 flex items-start gap-1.5 rounded-xl bg-slate-50 p-3 text-xs text-muted-foreground">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" /> नोट: SMS सत्यापन पूरा हो चुका है। कृपया भुगतान की पुष्टि करें।
          </div>
          <button data-testid={`auto-sms-${d.id}`} onClick={() => autoSms(d)} className="w-full mt-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 py-2.5 text-sm font-semibold hover:bg-emerald-100 transition-colors">
            ⚡ ऑटो SMS सत्यापन (Free)
          </button>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Button variant="outline" data-testid={`deposit-reject-${d.id}`} onClick={() => act(() => api.post(`/admin/deposits/${d.id}`, { action: "reject" }), "अस्वीकृत")} className="h-14 rounded-2xl border-2 border-red-300 text-red-600 hover:bg-red-50 text-base font-bold">
              <Sparkles className="h-5 w-5 mr-1.5" /> अस्वीकार करें
            </Button>
            <Button data-testid={`deposit-approve-${d.id}`} onClick={() => act(() => api.post(`/admin/deposits/${d.id}`, { action: "approve" }), "स्वीकृत — जमा जोड़ा गया")} className="h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-base font-bold">
              <CheckCircle2 className="h-5 w-5 mr-1.5" /> स्वीकार करें
            </Button>
          </div>
        </div>
      ))}

      {/* Withdrawal cards */}
      {pendingWithdrawals.map((w) => (
        <div key={w.id} data-testid={`verify-withdraw-${w.id}`} className="rounded-3xl bg-white p-5 shadow-md border border-slate-100">
          <div className="flex items-center gap-3">
            <Avatar name={w.member_name} size={48} />
            <div className="flex-1">
              <p className="font-display font-bold text-xl">{nameHi[w.member_id] || w.member_name} <span className="text-xs text-red-500">(निकासी)</span></p>
              <p className="font-mono font-bold text-2xl">{inr(w.amount)}</p>
              <p className="text-xs text-muted-foreground">{w.note || "—"}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Button variant="outline" data-testid={`withdraw-reject-${w.id}`} onClick={() => act(() => api.post(`/admin/withdraw-requests/${w.id}`, { action: "reject" }), "अस्वीकृत")} className="h-14 rounded-2xl border-2 border-red-300 text-red-600 hover:bg-red-50 text-base font-bold">
              <XCircle className="h-5 w-5 mr-1.5" /> अस्वीकार
            </Button>
            <Button data-testid={`withdraw-approve-${w.id}`} onClick={() => act(() => api.post(`/admin/withdraw-requests/${w.id}`, { action: "approve" }), "स्वीकृत")} className="h-14 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-base font-bold">
              <CheckCircle2 className="h-5 w-5 mr-1.5" /> स्वीकार
            </Button>
          </div>
        </div>
      ))}

      <p className="text-center text-xs text-muted-foreground pt-2">सभी निर्णय लॉग किए जाएंगे • Apna Samooh v2.1</p>
    </div>
  );
}
